import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import api from '@/lib/api';
import { getToken, setToken, clearToken } from '@/lib/tokenStore';

const AuthContext = createContext(null);

function makeSessionWsUrl() {
  const token = getToken();
  if (!token) return null;
  const backend = process.env.REACT_APP_BACKEND_URL;
  const wsBase = backend.replace(/^http/, 'ws');
  return `${wsBase}/api/ws/session?token=${encodeURIComponent(token)}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [hasSub, setHasSub] = useState(false);
  const [subExpiresAt, setSubExpiresAt] = useState(null);
  const [accessStatus, setAccessStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const wsRef = useRef(null);

  // Silently drop the session: clear token, redirect. No toast, no warning.
  const forceLogout = useCallback(() => {
    clearToken();
    setUser(null);
    setHasSub(false);
    setSubExpiresAt(null);
    setAccessStatus(null);
    if (!window.location.pathname.startsWith('/login')) {
      window.location.replace('/login');
    }
  }, []);

  const openSessionChannel = useCallback(() => {
    // Close any prior socket first
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    }
    const url = makeSessionWsUrl();
    if (!url) return;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg?.type === 'session_invalidated') forceLogout();
      } catch { /* ignore */ }
    };
    ws.onclose = (e) => {
      // 4401 = server explicitly rejected due to invalidation
      if (e.code === 4401) forceLogout();
    };
  }, [forceLogout]);

  const refresh = useCallback(async () => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setHasSub(data.has_active_subscription);
      setSubExpiresAt(data.subscription_expires_at || null);
      setAccessStatus(data.access_status || null);
      openSessionChannel();
    } catch (e) {
      clearToken();
      setUser(null);
      if (e?.response?.status !== 401) console.error('Auth refresh failed', e);
    } finally {
      setLoading(false);
    }
  }, [openSessionChannel]);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    setHasSub(data.has_active_subscription);
    setSubExpiresAt(data.subscription_expires_at || null);
    setAccessStatus(data.access_status || null);
    openSessionChannel();
    return data;
  }, [openSessionChannel]);

  const register = useCallback(async (payload) => {
    const { data } = await api.post('/auth/register', payload);
    setToken(data.token);
    setUser(data.user);
    setHasSub(data.has_active_subscription);
    setSubExpiresAt(data.subscription_expires_at || null);
    setAccessStatus(data.access_status || null);
    openSessionChannel();
    return data;
  }, [openSessionChannel]);

  const logout = useCallback(() => {
    if (wsRef.current) {
      try { wsRef.current.close(); } catch { /* noop */ }
      wsRef.current = null;
    }
    clearToken();
    setUser(null);
    setHasSub(false);
    setSubExpiresAt(null);
    setAccessStatus(null);
  }, []);

  const value = useMemo(
    () => ({ user, hasSub, subExpiresAt, accessStatus, loading, login, register, logout, refresh, setUser }),
    [user, hasSub, subExpiresAt, accessStatus, loading, login, register, logout, refresh]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
